import React, { useEffect, useRef, useState } from 'react';
import { Table, Button, Divider, Stack, Panel, IconButton, Modal, Progress } from 'rsuite';
import TrashIcon from '@rsuite/icons/Trash';
import { AxiosError } from 'axios';
import { BaseErrorModel, PagedResponseModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import { ObjectStorageFileCategory, ObjectStorageFileType } from '@/models/storage.model';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import ImageCell from '@/components/ImageCell';
import TablePagination from '@/components/TablePagination';
import PageContent from '@/components/PageContent';
import { AdvanceSharePackItem, AdvanceSharePackItemPaginationFilterRequest } from '@/models/advance-share-pack.model';
import { deleteAdvanceSharePackItem, getPagedAdvanceSharePackItems, upsertAdvanceSharePackItem } from '@/services/advance-share-pack.service';
import { uploadToStorage_v2 } from '@/services/storage.service';

const { Column, HeaderCell, Cell } = Table;

interface AdvanceSharePackItemListProps {
  packId: number;
}

const AdvanceSharePackItemList: React.FC<AdvanceSharePackItemListProps> = ({ packId }) => {
  const [filterReq, setFilterReq] = useState<AdvanceSharePackItemPaginationFilterRequest>({ ...new PaginationFilterRequest(), packId });
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<AdvanceSharePackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadDone, setUploadDone] = useState(0);
  const [uploadFailed, setUploadFailed] = useState(0);
  const uploading = uploadTotal > 0 && uploadDone + uploadFailed < uploadTotal;
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: AdvanceSharePackItemPaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<AdvanceSharePackItem> = await getPagedAdvanceSharePackItems(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching pack items:${errorModel?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length == 0) {
      return;
    }

    setUploadTotal(files.length);
    setUploadDone(0);
    setUploadFailed(0);

    let doneCount = 0;
    let failedCount = 0;

    // Uploaded one by one (sequential) so the API/storage isn't hit with a burst of parallel uploads.
    for (const file of files) {
      try {
        const uploaded = await uploadToStorage_v2({ file, category: ObjectStorageFileCategory.AdvanceShare, type: ObjectStorageFileType.Image });
        await upsertAdvanceSharePackItem({ packId, imageUrl: uploaded.url });
        doneCount++;
        setUploadDone(doneCount);
      } catch (err) {
        failedCount++;
        setUploadFailed(failedCount);
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error uploading "${file.name}":${errorModel?.errorMessage}`);
      }
    }

    if (doneCount > 0) {
      toastSuccessMessage(`${doneCount} item(s) added${failedCount > 0 ? `, ${failedCount} failed` : ''}.`);
      fetchData(filterReq);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this item?')) {
      return;
    }
    try {
      const result = await deleteAdvanceSharePackItem(id);
      if (result.success) {
        toastSuccessMessage('Item deleted.');
        fetchData(filterReq);
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in deleting pack item:${errorModel?.errorMessage}`);
    }
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => {
          setUploadTotal(0);
          setUploadDone(0);
          setUploadFailed(0);
          setAddOpen(true);
        }}>
          Add Items
        </Button>
      </Stack>

      <Table autoHeight data={items} loading={loading}>
        <Column flexGrow={1} align="center">
          <HeaderCell>Image</HeaderCell>
          <ImageCell dataKey="imageUrl" />
        </Column>
        <Column flexGrow={1} align="center">
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={0.5} fixed="right" align="center">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: AdvanceSharePackItem) => (
              <IconButton onClick={() => handleDelete(rowData.id)} icon={<TrashIcon />} color="red" appearance="subtle" circle size="sm" />
            )}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({ ...pre, pageNumber: dataKey }))}
        onChangeLimit={dataKey => setFilterReq(pre => ({ ...pre, pageSize: dataKey }))}
      />

      <Modal backdrop="static" keyboard={false} open={addOpen} onClose={() => !uploading && setAddOpen(false)}>
        <Modal.Header>
          <Modal.Title>Add Pack Items</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Panel>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              multiple
              disabled={uploading}
              onChange={handleFilesSelected}
            />
            {uploadTotal > 0 && (
              <div style={{ marginTop: 20 }}>
                <Progress.Line
                  percent={Math.round(((uploadDone + uploadFailed) / uploadTotal) * 100)}
                  status={uploading ? 'active' : (uploadFailed > 0 ? 'fail' : 'success')}
                />
                <div>{uploadDone + uploadFailed} of {uploadTotal} processed{uploadFailed > 0 ? ` (${uploadFailed} failed)` : ''}</div>
              </div>
            )}
          </Panel>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setAddOpen(false)} appearance="subtle" disabled={uploading}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </PageContent>
  );
};

export default AdvanceSharePackItemList;
